import { createContext, useCallback, useContext, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';

export interface ConfirmOptions {
  title: string;
  /** What will happen; defaults to "This cannot be undone." for a destructive verb. */
  message?: React.ReactNode;
  confirmLabel?: string;
  color?: 'primary' | 'error' | 'warning';
}

/** Ask the person to confirm; resolves true when they do. A plain string is used as the title. */
type Ask = (request: string | ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Ask | null>(null);

const DESTRUCTIVE = new Set(['delete', 'remove', 'revoke', 'withdraw', 'discard', 'archive']);

function normalise(request: string | ConfirmOptions): Required<ConfirmOptions> {
  const o = typeof request === 'string' ? { title: request } : request;
  const verb = (o.title.trim().split(/\s+/)[0] ?? '').replace(/[^A-Za-z]/g, '');
  const destructive = DESTRUCTIVE.has(verb.toLowerCase());
  return {
    title: o.title,
    message: o.message ?? (destructive ? 'This cannot be undone.' : ''),
    confirmLabel: o.confirmLabel ?? (verb.length > 1 && verb.length < 12 ? verb : 'Confirm'),
    color: o.color ?? (destructive ? 'error' : 'primary'),
  };
}

/**
 * One confirmation dialog for the whole application, asked for through `useConfirm()`:
 * `if (await confirm('Delete this action?')) …`. The dialog names the consequence and the action,
 * which the browser's own prompt cannot, and it renders in the app's theme.
 */
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState<{ options: Required<ConfirmOptions>; resolve: (value: boolean) => void } | null>(null);

  const ask = useCallback<Ask>((request) => new Promise<boolean>((resolve) => {
    setPending({ options: normalise(request), resolve });
  }), []);

  const settle = (value: boolean) => {
    pending?.resolve(value);
    setPending(null);
  };

  return (
    <ConfirmContext.Provider value={ask}>
      {children}
      {pending && (
        <ConfirmDialog
          title={pending.options.title}
          message={pending.options.message}
          confirmLabel={pending.options.confirmLabel}
          color={pending.options.color}
          onConfirm={async () => settle(true)}
          onClose={() => settle(false)}
        />
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): Ask {
  const ask = useContext(ConfirmContext);
  if (!ask) throw new Error('useConfirm must be used inside ConfirmProvider.');
  return ask;
}
